import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push as Push } from 'redux-first-history';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import {
    palette,
    Panel,
    SecondaryButton,
    Tooltip,
    Typography,
} from '@dev/ee-components';
import { Info as InfoIcon } from '@material-ui/icons';
import { Link } from '@material-ui/core';
import styled from 'styled-components';

import { isApiBillingPlan } from '@/services/Secuirty';
import { showAccountStorage } from '@/services/tools/showAccountStorage';
import { SetToastPortalWrapper } from '@/store/toast/actions';
import { getContactsStore } from '@/store/contacts/actions';
import { getAccountStore } from '@/store/account/actions';
import { SetAnalyticsOpen } from '@/store/upload/actions';
import { ContactsPaths, BillingPaths, pathsParams } from '@/services/dictionaries/Paths';
import { useActivePlan } from '@/services/hooks/useActivePlan';
import { useSecurity } from '@/services/hooks/useSecurity';
import { getPathParams } from '../../../../helperFunctions';

const CloseButtonWrapper = styled.div`
    padding-top: 32px;
`;

const TextContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    justify-content: space-between;
    align-items: center;
`;

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    svg {
        fill: ${palette.gray[4]};
        margin: 0px 8px;
        width: 20px;
        height: 20px;
        vertical-align: sub;
    }
`;

const StorageContactsInfo = styled.div`
    margin-top: 64px;
`;

export const AnalyticsTable = (): JSX.Element => {
    const { t } = useTranslation();
    const { segmentname, listname } = useParams<{ segmentname: string; listname: string }>();
    const dispatch = useDispatch();
    const { Overview, GeneralInfo } = useSelector(getAccountStore);
    const { validationStatus } = useSelector(getContactsStore);

    const analiticsOptionsToDisplay = (): any[] => {
        //const { realUploaded, contactsleft, contactCount } = getContactInfoAfterUpload(validationStatus, account);
        const notAddedContacts = (validationStatus.ValidationStatus?.NotUploadedDuplicatedCount || 0) + (validationStatus.ValidationStatus?.NotUploadedSyntaxCount || 0);
        const existingContacts = validationStatus.Contacts - notAddedContacts - validationStatus.NewContacts;
        return [
            {
                title: t('uploadedcontacts'),
                tooltip: t('uploadedcontactstooltip'),
                value: validationStatus.Contacts,
                id: 'uploaded-contacts',
            },
            {
                title: t('newcontacts'),
                tooltip: t('newcontactstooltip'),
                value: validationStatus.NewContacts,
                id: 'new-contacts',
            },
            {
                title: t('existingcontacts'),
                tooltip: t('existingcontactstooltip'),
                value: existingContacts,
                id: 'existing-contacts',
            },
            {
                title: t('contactsnotadded'),
                tooltip: t('contactsnotaddedtooltip'),
                value: notAddedContacts,
                id: 'contacts-not-added',
            }
        ];
    };

    const optionsToDisplay = analiticsOptionsToDisplay();
    const rowsToDisplay = AnaliticsRows(optionsToDisplay);
    const maxContacts = Overview.MaxContacts
        ? Overview.MaxContacts
        : GeneralInfo.PlanMaxContacts;
    const { Name } = useActivePlan();
    const isApiPlan = isApiBillingPlan();
    const showStorage = showAccountStorage(
        isApiPlan,
        GeneralInfo.IsSub,
        Overview.MaxContacts,
        GeneralInfo.PlanMaxContacts
    );
    const { isReseller } = useSecurity();
    const selectPlanScreen = (productType = 'marketing') => BillingPaths.BillingPlanList.replace(
        pathsParams.productCode,
        productType.toLowerCase()
    );
    
    const closeButtonOnclick = ():void => {
        dispatch(SetToastPortalWrapper(false));
        dispatch(SetAnalyticsOpen(false));
        const [path, pathParam, pathName] = getPathParams(segmentname, listname);
        dispatch(
            Push(
                listname || segmentname
                    ? path.replace(pathParam, safelyEncodeBase64Url(pathName))
                    : ContactsPaths.ContactsOverview
            )
        );
    }

    return (
        <>
            <Typography variant="h1">{t('contactsuploaded')}</Typography>
            <div style={{ margin: '4rem 0' }}>
                <Typography variant="body1">{t('uploadcantakeamomentpleasecheckaddresses')}</Typography>
            </div>
            <Panel
                header={t('summary')}
                description={t('uploadsummarydesc')}
                elementPadding="24px 32px"
                headerPadding="24px 32px"
            >
                {rowsToDisplay}
            </Panel>
            {showStorage && <StorageContactsInfo>
                <Typography variant='body1'>
                    {t('storagecontactsinfo', { planName: Name, allowedContacts: maxContacts })}
                    {' '}{<Link
                            style={{ color: palette.indigo[6] }}
                            href={isReseller && GeneralInfo?.AccountPaymentUrl ? GeneralInfo.AccountPaymentUrl : selectPlanScreen(GeneralInfo.ProductType)}
                        >
                            {t('upgrade').toLocaleLowerCase()}
                        </Link>}
                {' '}{t('yourcurrentplan')}
                </Typography>
            </StorageContactsInfo>
            }
            <CloseButtonWrapper>
                <SecondaryButton
                    id="close-button"
                    text={t('close')}
                    onClick={closeButtonOnclick}
                />
            </CloseButtonWrapper>
        </>
    );
};

const AnaliticsRow = (props: { singleRow, key}): JSX.Element => {
    const { singleRow, key } = props;
    const typographyVariant = singleRow.id === 'uploaded-contacts' ? 'h3' : 'body1';
    return (
        <TextContainer key={`${singleRow?.title}-${key}`} id={singleRow?.id} className="add-tooltip">
            <Typography variant={typographyVariant}>
                <TitleWrapper>
                    {singleRow?.title}
                    <Tooltip
                        color={palette.basic.paper}
                        placement="left"
                        title={singleRow?.tooltip}
                        id="tooltip-uploaded"
                    >
                        <InfoIcon />
                    </Tooltip>
                </TitleWrapper>
            </Typography>
            <Typography variant="h3">{singleRow?.value}</Typography>
        </TextContainer>
    );
}

const AnaliticsRows = (optionsToDisplay): JSX.Element[] => 
    optionsToDisplay?.map(
        (singleRow, idx): JSX.Element => 
            <AnaliticsRow singleRow={singleRow} key={idx}/>
);